import { type CSSProperties, type RefCallback, useMemo, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { useBreakpointsContext } from '@air/provider-media-query';

import { useSelectionBarVisibility } from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarVisibility';
import { useIsWorkspaceAgentPanelAvailable } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelAvailable';
import { useIsWorkspaceAgentPanelOverAssetView } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelOverAssetView';
import { clampWorkspaceAgentPanelFloatRect } from '~/components/v2/components/workspace/workspace-agent-panel/utils/clampWorkspaceAgentPanelFloatRect';
import {
  getWorkspaceAgentPanelAssetViewClipStyle,
  getWorkspaceAgentPanelDockedLayout,
  getWorkspaceAgentPanelSlotWidthAnimationStyle,
  getWorkspaceAgentPanelUnmeasuredDockedClipStyle,
  getWorkspaceAgentPanelUnmeasuredDockedStyle,
  type WorkspaceAgentPanelDockedSpacerRect,
  type WorkspaceAgentPanelSlotWidthAnimationStyle,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPanelDockedLayout';
import { syncWorkspaceAgentPanelWidthToCSS } from '~/components/v2/components/workspace/workspace-agent-panel/utils/syncWorkspaceAgentPanelWidthToCSS';
import { WORKSPACE_AGENT_PANEL_UI2026_DOCKED_CARD_STYLE } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentPanelUi2026DockedCardStyle';
import {
  WORKSPACE_AGENT_PANEL_ABOVE_PORTAL_Z_INDEX,
  WORKSPACE_AGENT_PANEL_ASSET_VIEW_HEADER_TOP,
} from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';
import {
  isWorkspaceAgentPanelExpanded,
  isWorkspaceAgentPanelSlotOpen,
  useWorkspaceAgentPanelStore,
} from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useWorkspaceSelectionBarStore } from '~/components/v2/store/features/workspace/useWorkspaceSelectionBarStore';
import { WORKSPACE_PANEL_EDGE_PADDING, WORKSPACE_UI2026_SURFACE_GAP } from '~/constants/panelWidths';

/** Which entry point the feature offers right now. */
export type WorkspaceAgentPanelAffordance = 'panel' | 'minimized' | 'none';

/** Which surface the panel is sitting on, for the styling that has to agree with each. */
export type WorkspaceAgentPanelSurface = 'workspace' | 'asset-view';

type ViewportSize = {
  height: number;
  width: number;
};

export type WorkspaceAgentPanelViewport = {
  affordance: WorkspaceAgentPanelAffordance;
  /**
   * True while `dock` is the in-flow slot (`right`). Clip Presence keys off this, not `showSpacer`,
   * because a floating panel is expanded with the spacer down.
   */
  isSlotOpen: boolean;
  /**
   * True while the card is expanded (`right` or `float`). Clip enter keys off this rather than
   * `isSlotOpen` so docking from float does not count as opening from a collapsed slot.
   */
  isExpanded: boolean;
  /**
   * Viewport-fixed rect for the portalled panel. Docked workspace geometry is hidden at stored
   * width until the spacer has been measured so the portal (and on ui2026 the clip wrapper)
   * stay mounted. `null` only when the expanded card is not shown (minimized, no affordance).
   */
  panelStyle: CSSProperties | null;
  /**
   * ui2026 docked slot: a right-anchored overflow clip around the card. `null` when the card is
   * itself viewport-fixed (float, asset view, or pre-ui2026).
   */
  slotClipStyle: CSSProperties | null;
  /** Stored width + `--tw-animation-width` for CSS `animate-width-in` / `animate-width-out`. */
  slotWidthAnimationStyle: WorkspaceAgentPanelSlotWidthAnimationStyle;
  /**
   * Callback ref for the in-flow spacer. A callback (not `useRef`) so `ResizeObserver` stays
   * attached through Presence's exit — the node is the same until the slot width hits 0.
   */
  spacerRef: RefCallback<HTMLDivElement>;
  showSpacer: boolean;
  /**
   * Comes from here rather than being read again in the panel so the surface-dependent styling
   * cannot disagree with the geometry above, which is decided by the same predicate.
   */
  surface: WorkspaceAgentPanelSurface;
};

export type UseWorkspaceAgentPanelViewportOptions = {
  canvasOpen: boolean;
  ui2026: boolean;
};

type AreDockedSpacerRectsEqualParams = {
  current: WorkspaceAgentPanelDockedSpacerRect | null;
  next: WorkspaceAgentPanelDockedSpacerRect;
  ui2026: boolean;
};

const areDockedSpacerRectsEqual = ({ current, next, ui2026 }: AreDockedSpacerRectsEqualParams): boolean => {
  if (!current) {
    return false;
  }

  if (current.height !== next.height || current.insetRight !== next.insetRight || current.top !== next.top) {
    return false;
  }

  // ui2026 clip width is the stored width (CSS-animated), so left/width changing every animation
  // frame must not re-render the panel. Pre-ui2026 still mirrors the spacer box.
  if (ui2026) {
    return true;
  }

  return current.left === next.left && current.width === next.width;
};

/**
 * Positions the panel, which lives in a body-level portal so it can paint above the asset modal
 * (also a body-level portal — see `PrivateWorkspaceAgentPanel`). Being out of flow, it can no
 * longer be laid out by the workspace, so on workspace pages it tracks an in-flow spacer left in
 * its slot. On ui2026 that spacer is a sibling of the chrome card, so the whole workspace card
 * (header included) is squeezed; the portalled card stays at stored width inside a right-anchored
 * overflow clip whose width is CSS `animate-width-in` / `animate-width-out` (same as
 * `AdDetailsSidebarContainer`, `WorkspaceSidebarContainer`, and `CUSTOM_FIELDS_PANEL`). Pre-ui2026
 * still mirrors the spacer box and clears the status-bar strip. There is no second definition of
 * the panel's geometry.
 *
 * Over the asset view there is nothing to mirror, so it takes viewport insets instead: the modal's
 * own 8px content padding on three sides, and the height of its header bar on top.
 */
export const useWorkspaceAgentPanelViewport = ({
  canvasOpen,
  ui2026,
}: UseWorkspaceAgentPanelViewportOptions): WorkspaceAgentPanelViewport => {
  const { assetViewOpen, isWorkspaceAgentPanelOverAssetView: isOverAssetView } =
    useIsWorkspaceAgentPanelOverAssetView();
  const { canShowAgentStartTask, isHeaderChromeRoute } = useIsWorkspaceAgentPanelAvailable();
  const { hasSelection } = useSelectionBarVisibility();
  const pageOwnedSelectionBarMountCount = useWorkspaceSelectionBarStore(
    (state) => state.pageOwnedSelectionBarMountCount,
  );
  const { isAboveMediumScreen } = useBreakpointsContext();
  // Selection-bar Start task is the reopen control on pages without header chrome. Keep the panel
  // while that bar is showing it; stand down again when the selection (and the button) go away.
  const selectionBarShowsStartTask =
    canShowAgentStartTask && hasSelection && pageOwnedSelectionBarMountCount === 0 && !assetViewOpen;
  const isElevatedAboveOnboarding = useWorkspaceAgentPanelStore((state) => state.isElevatedAboveOnboarding);
  // The intro sky covers the page. Standing down here leaves that sky with nothing on it.
  const shouldStandDown =
    !isElevatedAboveOnboarding && (canvasOpen || (!isHeaderChromeRoute && !selectionBarShowsStartTask));
  const dock = useWorkspaceAgentPanelStore((state) => state.dock);
  const width = useWorkspaceAgentPanelStore((state) => state.width);
  // ui2026 layout reads `--workspace-agent-panel-width`. LD can flip ui2026 after persist hydrates;
  // re-apply the stored width so a late-mounting `SidebarResizer` cannot reset the variable to 400.
  useIsomorphicLayoutEffect(() => {
    if (ui2026) {
      syncWorkspaceAgentPanelWidthToCSS(width);
    }
  }, [ui2026, width]);
  const floatLeft = useWorkspaceAgentPanelStore((state) => state.floatLeft);
  const floatTop = useWorkspaceAgentPanelStore((state) => state.floatTop);
  const floatHeight = useWorkspaceAgentPanelStore((state) => state.floatHeight);
  const floatWidth = useWorkspaceAgentPanelStore((state) => state.floatWidth);
  const isSlotOpen = isWorkspaceAgentPanelSlotOpen({ dock });
  const isExpanded = isWorkspaceAgentPanelExpanded({ dock });

  const [spacerElement, setSpacerElement] = useState<HTMLDivElement | null>(null);
  const [spacerRect, setSpacerRect] = useState<WorkspaceAgentPanelDockedSpacerRect | null>(null);
  const [viewportSize, setViewportSize] = useState<ViewportSize | null>(null);

  // The gallery stays mounted under the asset modal. A spacer that grows only because that
  // modal is open squeezes the gallery, then closing the modal collapses it — a visible resize.
  // `isSlotOpen` is the docked slot only, so float does not keep the gutter either.
  const showSpacer = !shouldStandDown && isAboveMediumScreen && isSlotOpen;
  // Not gated on ui2026: pre-ui2026 has no clip, so the card must stay mounted and mirror the
  // shrinking spacer (ResizeObserver) through width-out. Gating this like `isAssetViewSlotExiting`
  // would drop `panelStyle` on minimize and leave an empty gutter for 150ms. ui2026 close is
  // Presence width-out on the clip instead; this flag still keeps geometry until that clip exits.
  const isDockedSlotExiting =
    !shouldStandDown && !isSlotOpen && !assetViewOpen && isAboveMediumScreen && spacerRect !== null && dock !== 'float';
  // Minimize on the asset view must keep the clip mounted through width-out. `isOverAssetView` is
  // false as soon as the slot closes, so this cannot reuse that flag. Only ui2026 has a clip —
  // without that gate, pre-ui2026 would keep a fully painted card over the modal with no Presence.
  const isAssetViewSlotExiting =
    ui2026 && !shouldStandDown && assetViewOpen && !isSlotOpen && isAboveMediumScreen && dock !== 'float';
  const isAssetViewClippedSlot = ui2026 && (isOverAssetView || isAssetViewSlotExiting);

  useIsomorphicLayoutEffect(() => {
    if (spacerElement) {
      return;
    }

    setSpacerRect(null);
  }, [spacerElement]);

  useIsomorphicLayoutEffect(() => {
    if (!spacerElement) {
      return;
    }

    const measure = () => {
      const { height, left, right, top, width: spacerWidth } = spacerElement.getBoundingClientRect();
      const nextRect: WorkspaceAgentPanelDockedSpacerRect = {
        height,
        insetRight: window.innerWidth - right,
        left,
        top,
        width: spacerWidth,
      };

      setSpacerRect((current) => (areDockedSpacerRectsEqual({ current, next: nextRect, ui2026 }) ? current : nextRect));
    };

    // `ResizeObserver` reports size, not position, so chrome that moved the spacer without
    // resizing it would go unnoticed. Nothing does that today — banners and the workspace header
    // both change its height when they appear — but the resize listener removes the assumption.
    const observer = new ResizeObserver(measure);

    observer.observe(spacerElement);
    window.addEventListener('resize', measure);
    measure();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [spacerElement, ui2026]);

  useIsomorphicLayoutEffect(() => {
    const measureViewport = () => {
      const nextSize = { height: window.innerHeight, width: window.innerWidth };

      setViewportSize((current) =>
        current?.height === nextSize.height && current.width === nextSize.width ? current : nextSize,
      );
    };

    measureViewport();
    window.addEventListener('resize', measureViewport);

    return () => {
      window.removeEventListener('resize', measureViewport);
    };
  }, []);

  const affordance = useMemo((): WorkspaceAgentPanelAffordance => {
    // Canvas has its own agent chat; pages without header Start task must not keep the card open.
    if (shouldStandDown) {
      return 'none';
    }

    if (isOverAssetView) {
      return 'panel';
    }

    // Minimized behind the asset modal: bottom chrome would be stranded, so the asset header's own
    // control is the way back in. A floating panel is already an overlay, so it stays mounted.
    if (assetViewOpen && !isExpanded) {
      return 'none';
    }

    if (!isAboveMediumScreen) {
      return 'none';
    }

    return isExpanded ? 'panel' : 'minimized';
  }, [assetViewOpen, isExpanded, isAboveMediumScreen, isOverAssetView, shouldStandDown]);

  const isDockedWorkspacePanel =
    (affordance === 'panel' || isDockedSlotExiting) && dock !== 'float' && !isOverAssetView && !isAssetViewSlotExiting;

  const slotWidthAnimationStyle = useMemo(
    (): WorkspaceAgentPanelSlotWidthAnimationStyle =>
      getWorkspaceAgentPanelSlotWidthAnimationStyle({ useCssWidthVariable: ui2026, width }),
    [ui2026, width],
  );

  const slotClipStyle = useMemo((): CSSProperties | null => {
    const baseStyle = (() => {
      if (isAssetViewClippedSlot) {
        return getWorkspaceAgentPanelAssetViewClipStyle(ui2026);
      }

      if (!isDockedWorkspacePanel || !ui2026) {
        return null;
      }

      if (!spacerRect) {
        if (isElevatedAboveOnboarding) {
          return {
            bottom: WORKSPACE_UI2026_SURFACE_GAP,
            right: WORKSPACE_UI2026_SURFACE_GAP,
            top: WORKSPACE_UI2026_SURFACE_GAP,
          };
        }

        return getWorkspaceAgentPanelUnmeasuredDockedClipStyle();
      }

      return getWorkspaceAgentPanelDockedLayout({ spacerRect, ui2026: true }).slotClipStyle;
    })();

    if (!baseStyle || !isElevatedAboveOnboarding) {
      return baseStyle;
    }

    return {
      ...baseStyle,
      visibility: 'visible',
      zIndex: WORKSPACE_AGENT_PANEL_ABOVE_PORTAL_Z_INDEX,
    };
  }, [isAssetViewClippedSlot, isDockedWorkspacePanel, isElevatedAboveOnboarding, spacerRect, ui2026]);

  // Memoized because this is the `style` prop of the memoized panel root: a fresh object here would
  // re-render the whole panel subtree on every unrelated store write and every spacer measurement.
  const panelStyle = useMemo((): CSSProperties | null => {
    if (affordance !== 'panel' && !isDockedSlotExiting && !isAssetViewSlotExiting) {
      return null;
    }

    if (dock === 'float') {
      if (!viewportSize) {
        return {
          maxWidth: floatWidth,
          visibility: 'hidden',
          width: floatWidth,
        };
      }

      const floatRect = clampWorkspaceAgentPanelFloatRect({
        height: floatHeight,
        left: floatLeft,
        top: floatTop,
        viewportHeight: viewportSize.height,
        viewportWidth: viewportSize.width,
        width: floatWidth,
      });

      return {
        height: floatRect.height,
        left: floatRect.left,
        maxWidth: floatRect.width,
        top: floatRect.top,
        width: floatRect.width,
      };
    }

    if (isOverAssetView || isAssetViewSlotExiting) {
      // @TODO(WORK-174): Interim geometry — the panel is held beneath the asset modal's header bar,
      // which spans the full width above it, the Strato Legacy prototype's arrangement and the
      // workspace layout's. A full-height panel beside the header is the intended end state.
      //
      // Restoring full height means, in order: `top: WORKSPACE_PANEL_EDGE_PADDING` here; the
      // modal's inset back to `paddingRight` on the root in `AssetModalDesktopLayout`, so the
      // header is again held off the panel it would otherwise sit under; and the spec's panel-top
      // assertion back to the header-actions-clear-the-panel form.
      // `WORKSPACE_AGENT_PANEL_ASSET_VIEW_HEADER_TOP` then has no consumer.
      if (ui2026) {
        return WORKSPACE_AGENT_PANEL_UI2026_DOCKED_CARD_STYLE;
      }

      return {
        bottom: WORKSPACE_PANEL_EDGE_PADDING,
        maxWidth: width,
        right: WORKSPACE_PANEL_EDGE_PADDING,
        top: WORKSPACE_AGENT_PANEL_ASSET_VIEW_HEADER_TOP,
        width,
      };
    }

    if (!isDockedWorkspacePanel) {
      return null;
    }

    if (!spacerRect) {
      return getWorkspaceAgentPanelUnmeasuredDockedStyle({ ui2026, width });
    }

    if (ui2026) {
      return WORKSPACE_AGENT_PANEL_UI2026_DOCKED_CARD_STYLE;
    }

    return getWorkspaceAgentPanelDockedLayout({ spacerRect, ui2026: false }).panelStyle;
  }, [
    affordance,
    dock,
    floatHeight,
    floatLeft,
    floatTop,
    floatWidth,
    isAssetViewSlotExiting,
    isDockedSlotExiting,
    isDockedWorkspacePanel,
    isOverAssetView,
    spacerRect,
    ui2026,
    viewportSize,
    width,
  ]);

  return {
    affordance,
    isExpanded,
    isSlotOpen,
    panelStyle,
    slotClipStyle,
    slotWidthAnimationStyle,
    spacerRef: setSpacerElement,
    showSpacer,
    surface: isOverAssetView || isAssetViewSlotExiting ? 'asset-view' : 'workspace',
  };
};
